import { ACK_NOTE_MAX, type Alarm } from "@fts/core";
import { useCallback, useState, type FormEvent, type KeyboardEvent } from "react";
import { Dialog } from "../components/Dialog";
import { Icon } from "../components/Icon";
import { useFeedback, useToast } from "../components/Toast";
import { useI18n } from "../i18n";
import { errorCode } from "../lib/errors";
import { useAlarmCount } from "../state/alarms";
import { useSession } from "../state/session";

/*
 * Faster alarm handling (UI phase B, ADR 0029). Acknowledging stays exactly what acknowledge_alarm() does
 * (status, who, when, optional note); only the way there is shorter: acknowledge from the list, an optimistic
 * update, and a rollback with a toast when the database refuses.
 */

/** The list of alarms the user came from (filters included), so "back" does not reset them. */
let lastListHash = "#/alarms";
export function rememberAlarmList(hash: string): void {
  if (hash.startsWith("#/alarms") && !/^#\/alarms\/[^?]/.test(hash)) lastListHash = hash;
}
export function alarmListHref(): string {
  return lastListHash;
}

/** What the row looks like right after acknowledging, before the database answers. */
export function optimisticAck(a: Alarm, userId: string | null, note: string | null, now: Date = new Date()): Alarm {
  return { ...a, status: "acknowledged", ackedBy: userId, ackedAt: now.toISOString(), ackNote: note };
}

export type AckOutcome = { ok: true; already: boolean } | { ok: false; error: unknown };

/**
 * Acknowledge with an optimistic update: `apply` receives the acknowledged alarm at once and the original
 * alarm again if the call fails (rollback), followed by an error toast. "Already acknowledged by someone
 * else" counts as done (the caller reloads to show who). The open-alarm badge is refreshed on success.
 */
export function useOptimisticAck() {
  const { t } = useI18n();
  const { provider, user } = useSession();
  const feedback = useFeedback();
  const toast = useToast();
  const { refresh } = useAlarmCount();
  return useCallback(
    async (
      alarm: Alarm,
      note: string | null,
      apply: (a: Alarm) => void,
      opts: {
        label: string;
        /** No success toast (the caller shows its own confirmation). */
        quiet?: boolean;
        /**
         * No error toast when this returns true at failure time: the caller shows the error inline (e.g. the
         * detail page while it is still open), so it is announced once. Checked late, after the call fails.
         */
        quietError?: () => boolean;
      },
    ): Promise<AckOutcome> => {
      apply(optimisticAck(alarm, user?.id ?? null, note));
      try {
        await provider.acknowledgeAlarm(alarm.id, note);
        if (!opts.quiet) feedback.ok(t("toast.alarmAcknowledged", { alarm: opts.label }));
        void refresh();
        return { ok: true, already: false };
      } catch (e) {
        if (errorCode(e) === "already_acknowledged") {
          toast({ message: t("toast.alarmAlreadyAcked", { alarm: opts.label }), tone: "info" });
          void refresh();
          return { ok: true, already: true };
        }
        apply(alarm);
        if (!opts.quietError?.()) feedback.fail(e, t("toast.alarmAckFailed", { alarm: opts.label }));
        return { ok: false, error: e };
      }
    },
    [provider, user, feedback, toast, refresh, t],
  );
}

/** Ctrl/⌘ + Enter in the note submits the form (Enter alone is a new line). */
export function submitOnCtrlEnter(e: KeyboardEvent<HTMLTextAreaElement>): void {
  if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
    e.preventDefault();
    e.currentTarget.form?.requestSubmit();
  }
}

/** Acknowledge from the list: note (optional) and confirm, without opening the detail page. */
export function QuickAckDialog({
  alarm,
  title,
  vehicle,
  time,
  onClose,
  onSubmit,
}: {
  alarm: Alarm;
  title: string;
  vehicle: string;
  time: string;
  onClose: () => void;
  onSubmit: (note: string | null) => void;
}) {
  const { t } = useI18n();
  const [note, setNote] = useState("");
  const tooLong = note.trim().length > ACK_NOTE_MAX;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (tooLong) return;
    onSubmit(note.trim() || null);
  };
  return (
    <Dialog
      title={t("alarmQuick.title")}
      description={`${title} · ${vehicle} · ${time}`}
      onClose={onClose}
      onSubmit={submit}
      footer={
        <>
          <a href={`#/alarms/${encodeURIComponent(alarm.id)}`} className="button button-ghost dialog-footer-start" onClick={onClose}>
            {t("alarmQuick.openDetail")}
          </a>
          <button type="button" className="button button-secondary" onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="button button-primary" disabled={tooLong}>
            <Icon name="check" size={16} />
            {t("alarmAck.button")}
          </button>
        </>
      }
    >
      {alarm.type === "impact" && <p className="dialog-text muted small">{t("alarm.noBlock")}</p>}
      <div className="field">
        <label htmlFor="quick-ack-note">
          {t("alarmAck.noteLabel")}
          <span className="field-optional"> · {t("form.optional")}</span>
        </label>
        <textarea
          id="quick-ack-note"
          rows={3}
          value={note}
          aria-invalid={tooLong || undefined}
          aria-describedby={tooLong ? "quick-ack-hint quick-ack-error" : "quick-ack-hint"}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={submitOnCtrlEnter}
          data-autofocus
        />
        <p id="quick-ack-hint" className="field-hint">
          {t("alarmAck.noteHint")} {t("alarmQuick.shortcut")}
        </p>
        {tooLong && (
          <p id="quick-ack-error" className="field-error">
            {t("alarmAck.tooLong")}
          </p>
        )}
      </div>
    </Dialog>
  );
}
